<template>
  <a-table
    class="table datatable thead-light"
    :columns="columns"
    :data-source="dataSource"
    :loading="loading"
    :pagination="pagination"
    row-key="id"
    @change="onChange"
  >
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'User'">
        <div>
          <div class="fw-semibold">{{ record.user_name || "-" }}</div>
          <div class="fs-12 text-muted">{{ record.user_email }}</div>
        </div>
      </template>
      <template v-else-if="column.key === 'ActorType'">
        <div>
          <span class="badge" :class="actorBadgeClass(record.actor_type)">
            {{ record.actor_label || "User" }}
          </span>
          <div v-if="record.actor_roles?.length" class="fs-12 text-muted mt-1">
            {{ record.actor_roles.join(", ") }}
          </div>
        </div>
      </template>
      <template v-else-if="column.key === 'Action'">
        <span
          class="badge"
          :class="{
            'bg-success': record.action === 'created',
            'bg-warning': record.action === 'updated',
            'bg-danger': record.action === 'deleted',
            'bg-info': record.action === 'restored',
          }"
        >
          {{ record.action.toUpperCase() }}
        </span>
      </template>
      <template v-else-if="column.key === 'Description'">
        <span class="text-truncate d-inline-block" style="max-width: 280px">
          {{ record.description }}
        </span>
      </template>
    </template>
  </a-table>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  rows: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  total: { type: Number, default: 0 },
  pageSize: { type: Number, default: 25 },
  current: { type: Number, default: 1 },
});

const emit = defineEmits(["change"]);

const columns = [
  {
    title: "Date / Time",
    dataIndex: "created_at",
    key: "CreatedAt",
    sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
  },
  {
    title: "Module",
    dataIndex: "model",
    key: "Model",
    sorter: (a, b) => (a.model || "").localeCompare(b.model || ""),
  },
  {
    title: "Record ID",
    dataIndex: "auditable_id",
    key: "RecordId",
    sorter: (a, b) => (a.auditable_id || 0) - (b.auditable_id || 0),
  },
  {
    title: "Action",
    dataIndex: "action",
    key: "Action",
  },
  {
    title: "Actor Type",
    dataIndex: "actor_label",
    key: "ActorType",
  },
  {
    title: "User",
    dataIndex: "user_name",
    key: "User",
  },
  {
    title: "IP Address",
    dataIndex: "ip_address",
    key: "Ip",
  },
  {
    title: "Description",
    dataIndex: "description",
    key: "Description",
  },
];

const dataSource = computed(() =>
  (props.rows || []).map((r) => ({
    id: r.id,
    created_at: r.created_at,
    model: r.model,
    auditable_id: r.auditable_id,
    action: r.action,
    actor_type: r.actor_type,
    actor_label: r.actor_label,
    actor_roles: r.actor_roles || [],
    user_name: r.user?.name,
    user_email: r.user?.email,
    ip_address: r.ip_address,
    description: r.description,
  }))
);

const pagination = computed(() => ({
  total: props.total,
  pageSize: props.pageSize,
  current: props.current,
  showTotal: (t) => `Total ${t} items`,
}));

function onChange(pagination, filters, sorter) {
  emit("change", pagination, filters, sorter);
}

function actorBadgeClass(actorType) {
  if (actorType === "superadmin") return "badge-soft-danger";
  if (actorType === "tenant_admin") return "badge-soft-warning";
  if (actorType === "system") return "badge-soft-secondary";
  return "badge-soft-info";
}
</script>
